import React from "react";
import { observer } from "mobx-react";
import {
  generatePath,
  useParams,
  useNavigate,
} from "react-router-dom";

import ResourcesStore from "stores/ResourcesStore";
import RecipeDetailsStore from "stores/RecipeDetailsStore";
import WorkoutDetailsStore from "stores/WorkoutDetailsStore";

import { NavListItem } from "components/Layout";
import { NavLinkItemType } from "components/Layout/NavListItem/NavListItem";

import { capitalize } from "utils/common";

import ROUTES from "navigation/routes";
type Props = { workoutLabel?: string };

const NavList: React.FC<Props> = observer(() => {
  const params = useParams<{
    workoutId?: string;
    workoutInWorkoutCategoryId?: string;
    movementId?: string;
    recipeId?: string;
    quickLinkId?: string;
  }>();
  const navigate = useNavigate();


  const workoutId = params.workoutId ? Number.parseInt(params.workoutId) : undefined;
  const workoutInWorkoutCategoryId = params.workoutInWorkoutCategoryId
    ? Number.parseInt(params.workoutInWorkoutCategoryId)
    : undefined;
  const movementId = params.movementId ? Number.parseInt(params.movementId) : undefined;
  const recipeId = params.recipeId ? Number.parseInt(params.recipeId) : undefined;
  const quickLinkId = params.quickLinkId ? Number.parseInt(params.quickLinkId) : undefined;

  const firstItem: NavLinkItemType = {
    title: "Today",
    onClick: () => navigate(generatePath(ROUTES.TODAY)),
  };

  const workoutTitle = React.useMemo(() => {
    const title =
      WorkoutDetailsStore.details?.label || WorkoutDetailsStore.details?.title;
    return title ? capitalize(title) : undefined;
  }, [WorkoutDetailsStore.details?.label, WorkoutDetailsStore.details?.title]);

  const movementTitle = React.useMemo(() => {
    return movementId
      ? capitalize(
          WorkoutDetailsStore.getMovementDetails(movementId)?.title,
        )
      : undefined;
  }, [movementId]);

  const recipeTitle = React.useMemo(() => {
    return RecipeDetailsStore.details?.title
      ? capitalize(RecipeDetailsStore.details?.title)
      : undefined;
  }, [RecipeDetailsStore.details?.title]);

  const quickLinkTitle = React.useMemo(() => {
    return ResourcesStore.resourceDetails?.title
      ? capitalize(ResourcesStore.resourceDetails?.title)
      : undefined;
  }, [ResourcesStore.resourceDetails?.title]);

  const onClick = (route: string, routeParams?: any) => () => {
    navigate(generatePath(route, routeParams));
  };

  const navListItems = React.useMemo(() => {
    if (workoutId) {
      const secondItem =
        workoutId && workoutInWorkoutCategoryId !== undefined
          ? {
              title: workoutTitle,
              onClick: onClick(ROUTES.TODAY_WORKOUT_DETAILS, {
                workoutId,
                workoutInWorkoutCategoryId,
              }),
            }
          : undefined;
      const thirdItem =
        movementId && workoutInWorkoutCategoryId !== undefined
          ? {
              title: movementTitle,
              onClick: onClick(
                ROUTES.TODAY_WORKOUT_DETAILS_MOVEMENT_DETAILS,
                {
                  workoutId,
                  movementId,
                  workoutInWorkoutCategoryId,
                },
              ),
            }
          : undefined;

      return [firstItem, secondItem, thirdItem].filter(Boolean);
    }

    if (recipeId) {
      const secondItem = recipeId
        ? {
            title: recipeTitle,
            onClick: onClick(ROUTES.TODAY_RECIPE_DETAILS, {
              recipeId,
            }),
          }
        : undefined;

      return [firstItem, secondItem].filter(Boolean);
    }

    if (quickLinkId) {
      const secondItem = quickLinkId
        ? {
            title: quickLinkTitle,
            onClick: onClick(ROUTES.TODAY_QUICK_LINK_DETAILS, {
              quickLinkId,
            }),
          }
        : undefined;

      return [firstItem, secondItem].filter(Boolean);
    }

    return [];
  }, [
    workoutId,
    workoutInWorkoutCategoryId,
    movementId,
    recipeId,
    quickLinkId,
    workoutTitle,
    movementTitle,
    recipeTitle,
    quickLinkTitle,
    firstItem,
  ]);

  return (
    <>
      {navListItems.map((item: any, index: number) => (
        <NavListItem
          key={`${index}-${item.title}`}
          title={item.title}
          onClick={item.onClick}
          isLastItem={index === navListItems.length - 1}
        />
      ))}
    </>
  );
});

export default NavList;
